import type { IChecklistItemRow } from "@mctypes/checklist.types";
import type { IOtherAttributeConfigApi, IOtherAttributesData } from "@mcapps/shared/schemas/otherAttributesSchema";
import type { ColDef } from "ag-grid-community";
import { IconPencil } from "@tabler/icons-react";
import { getAgGridOtherAttributeColumns } from "../../../../components/OtherAttributes/otherAttributeColumns";

export type UpdateChecklistOtherAttribute = (
  itemId: string,
  key: string,
  value: NonNullable<IOtherAttributesData>[string],
) => Promise<void>;

export const getChecklistOtherAttributeColumns = (
  config: IOtherAttributeConfigApi,
  hiddenColumns: string[],
  update: UpdateChecklistOtherAttribute,
): ColDef<IChecklistItemRow>[] =>
  getAgGridOtherAttributeColumns({
    otherAttributesConfig: config,
    columnContext: {
      handleUpdateField: (id, field, value) => update(id, field.replace("otherAttributesData.", ""), value),
      hideAndSort: (field) => ({ hide: hiddenColumns.includes(field) }),
      isOtherAttributeCellUnlocked: () => false,
      lockOtherAttributeCell: () => {},
      unlockOtherAttributeCell: () => {},
    },
  }).map((column) => {
    const key = column.field!.replace("otherAttributesData.", "");
    const attribute = config[key];
    const readOnly = attribute.locked === true || ["FORMULA", "MATH_FORMULA"].includes(attribute.typeOfValue);
    const Renderer = column.cellRenderer;
    return {
      ...(column as ColDef<IChecklistItemRow>),
      colId: column.field,
      field: column.field as ColDef<IChecklistItemRow>["field"],
      editable: ({ data }) => data?.originalItem != null && !readOnly && attribute.typeOfValue !== "BOOLEAN",
      singleClickEdit: true,
      valueGetter: (params) => {
        if (params.data?.originalItem == null) return null;
        return typeof column.valueGetter === "function"
          ? column.valueGetter(params)
          : params.data.otherAttributesData?.[key] ?? null;
      },
      cellRenderer: (params) => {
        if (params.data?.originalItem == null) return null;
        if (readOnly || !Renderer) return params.value == null ? "" : String(params.value);
        if (attribute.typeOfValue === "BOOLEAN") return <Renderer {...params} />;
        const empty =
          params.value == null || params.value === "" || (Array.isArray(params.value) && params.value.length === 0);
        return (
          <div className="checklist-attribute-editable" title="Klikk for å redigere">
            <div className="checklist-attribute-value">
              {empty ? (
                <span className="checklist-attribute-placeholder">Legg til verdi</span>
              ) : (
                <Renderer {...params} />
              )}
            </div>
            <button
              type="button"
              className="checklist-attribute-edit-button"
              aria-label={`Rediger ${attribute.label}`}
              title="Rediger"
              onClick={(event) => {
                event.stopPropagation();
                if (params.node.rowIndex == null) return;
                params.api.startEditingCell({ rowIndex: params.node.rowIndex, colKey: column.field! });
              }}
            >
              <IconPencil size={14} aria-hidden="true" />
            </button>
          </div>
        );
      },
      valueSetter: (params) => {
        if (
          params.data?.originalItem == null ||
          readOnly ||
          JSON.stringify(params.oldValue) === JSON.stringify(params.newValue)
        )
          return false;
        const value = attribute.typeOfValue === "DATE" && params.newValue === "" ? null : params.newValue ?? null;
        void update(params.data._id, key, value);
        // RTK Query owns row data, including optimistic updates and rollback.
        return false;
      },
    };
  });
